import { Component } from '@theme/component';

/**
 * A search box that filters the option list of a single facet (e.g. Brand, Color)
 * client-side as the user types. Lives inside a `facet-inputs-component` and operates
 * on the sibling option items annotated with `[data-facet-value-label]`.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The text input.
 * @property {HTMLElement} [empty] - The "no results" message.
 *
 * @extends Component<Refs>
 */
export default class FacetSearchComponent extends Component {
  requiredRefs = ['searchInput'];

  /** @type {HTMLElement[]} */
  #items = [];

  /** @type {HTMLElement | null} */
  #showMoreButton = null;

  connectedCallback() {
    super.connectedCallback();
    this.#cacheItems();
  }

  /**
   * Caches the option items and their original show-more hidden class so the
   * list can be restored when the search is cleared.
   */
  #cacheItems() {
    const scope = this.closest('facet-inputs-component') ?? this.parentElement;
    if (!scope) return;

    this.#items = Array.from(scope.querySelectorAll('[data-facet-value-label]'));
    this.#showMoreButton = scope.querySelector('.show-more__button');

    for (const item of this.#items) {
      if (item.classList.contains('hidden')) {
        item.dataset.fsHiddenClass = 'hidden';
      } else if (item.classList.contains('mobile:hidden')) {
        item.dataset.fsHiddenClass = 'mobile:hidden';
      }
    }
  }

  /**
   * Filters the option list based on the current input value.
   * Bound via `on:input="/filterOptions"`.
   */
  filterOptions = () => {
    const query = this.refs.searchInput.value.trim().toLowerCase();

    if (!query) {
      this.reset();
      return;
    }

    // Re-cache in case the list was re-rendered (Section Rendering API).
    if (!this.#items.length) this.#cacheItems();

    this.setAttribute('data-fs-searching', '');
    this.#showMoreButton?.classList.add('hidden');

    let matches = 0;
    for (const item of this.#items) {
      const label = item.dataset.facetValueLabel ?? '';
      if (label.includes(query)) {
        matches += 1;
        item.classList.remove('fs-hide', 'hidden', 'mobile:hidden');
      } else {
        item.classList.add('fs-hide');
      }
    }

    this.refs.empty?.classList.toggle('hidden', matches > 0);
  };

  /**
   * Swallows the native `change` event of the search input so it never bubbles to
   * the parent `facet-inputs-component`, which would otherwise trigger an unwanted
   * section re-render (and leave the list stuck in its filtered state, since the
   * option `<li>`s are marked `data-skip-node-update`).
   * Bound via `on:change="/preventChange"` on the input.
   *
   * @param {Event} event
   */
  preventChange = (event) => {
    event.stopPropagation();
  };

  /**
   * Clears the search and restores the original list state.
   * Bound via `on:click="/reset"` on the reset button.
   */
  reset = () => {
    this.refs.searchInput.value = '';
    this.removeAttribute('data-fs-searching');
    this.#showMoreButton?.classList.remove('hidden');
    this.refs.empty?.classList.add('hidden');

    for (const item of this.#items) {
      item.classList.remove('fs-hide');
      const original = item.dataset.fsHiddenClass;
      if (original) item.classList.add(original);
    }

    this.refs.searchInput.focus();
  };
}

if (!customElements.get('facet-search-component')) {
  customElements.define('facet-search-component', FacetSearchComponent);
}
